import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Search, SlidersHorizontal } from 'lucide-react';
import { Button, Card, Input, Select } from '@/ui/primitives';
import { PageShell, Stack } from '@/ui/layout';
import { cn } from '@/lib/utils';
import {
  useResetPasswordAdmin,
  useUpdateUser,
  useUsers,
} from '@/modules/usuarios/api';
import { CreateUserModal } from '@/modules/usuarios/components/CreateUserModal';
import { EditUserModal } from '@/modules/usuarios/components/EditUserModal';
import { UsersStats } from '@/modules/usuarios/components/UsersStats';
import { UsersTable } from '@/modules/usuarios/components/UsersTable';
import { ROLES, ROLE_LABEL, type User } from '@/modules/usuarios/schemas';

export const Route = createFileRoute('/_authed/usuarios')({
  component: UsuariosPage,
});

function UsuariosPage() {
  const { data: users = [], isLoading } = useUsers();
  const update = useUpdateUser();
  const resetPw = useResetPasswordAdmin();

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterWrapRef = useRef<HTMLDivElement | null>(null);
  const activeCount = (roleFilter ? 1 : 0) + (statusFilter ? 1 : 0);

  // Fecha popover ao clicar fora ou ESC — paridade com /clientes e /crm
  useEffect(() => {
    if (!filtersOpen) return undefined;
    function onDown(e: MouseEvent) {
      if (filterWrapRef.current && !filterWrapRef.current.contains(e.target as Node)) {
        setFiltersOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setFiltersOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [filtersOpen]);

  function toggleActive(u: User) {
    const next = !u.active;
    if (
      next === false &&
      !confirm(`Desativar ${u.name}? O usuário não conseguirá mais entrar.`)
    )
      return;
    update.mutate({ id: u.id, patch: { active: next } });
  }

  function resetPassword(u: User) {
    const newPw = prompt(
      `Nova senha para ${u.name} (mínimo 8 caracteres, 1 maiúscula, 1 número):`,
    );
    if (!newPw) return;
    resetPw.mutate({ userId: u.id, new_password: newPw });
  }

  return (
    <PageShell
      eyebrow="Admin"
      title="Usuários"
      lead="Gerencie usuários, papéis e acessos."
      actions={
        <Button variant="primary" onClick={() => setCreateOpen(true)}>
          Convidar usuário
        </Button>
      }
    >
      <Stack gap="lg">
        <UsersStats users={users} />

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 md:max-w-md">
            <Search
              size={14}
              strokeWidth={1.5}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou e-mail"
              className="pl-8"
            />
          </div>

          <div ref={filterWrapRef} className="relative">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 h-7 rounded-full text-[0.78rem]',
                'border border-border transition-colors',
                activeCount > 0 || filtersOpen
                  ? 'bg-[rgba(217,119,87,0.1)] text-accent border-transparent'
                  : 'bg-elevated text-text-2 hover:bg-surface2 hover:text-text',
              )}
            >
              <SlidersHorizontal size={13} strokeWidth={1.5} />
              Filtrar por
              {activeCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 ml-1 rounded-full bg-accent text-[var(--on-solid)] text-[0.62rem] font-bold leading-none">
                  {activeCount}
                </span>
              )}
            </button>

            {filtersOpen && (
              <div
                className={cn(
                  'absolute top-[calc(100%+6px)] left-0 z-50',
                  'bg-surface border border-border rounded-xl shadow-[var(--shadow-over)]',
                  'p-4 min-w-[260px] flex flex-col gap-3',
                )}
              >
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="usr-flt-role"
                    className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                  >
                    Papel
                  </label>
                  <Select
                    id="usr-flt-role"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="text-[0.82rem]"
                  >
                    <option value="">Todos</option>
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="usr-flt-status"
                    className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                  >
                    Status
                  </label>
                  <Select
                    id="usr-flt-status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="text-[0.82rem]"
                  >
                    <option value="">Todos</option>
                    <option value="ativo">Ativos</option>
                    <option value="inativo">Inativos</option>
                  </Select>
                </div>

                {activeCount > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setRoleFilter('');
                      setStatusFilter('');
                    }}
                    className="text-[0.75rem] text-text-2 hover:text-accent text-left mt-1"
                  >
                    Limpar filtros
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <Card padding="lg">
            <p className="text-sm text-text-muted">Carregando usuários…</p>
          </Card>
        ) : (
          <UsersTable
            users={users}
            search={search}
            roleFilter={roleFilter}
            statusFilter={statusFilter}
            onEdit={setEditing}
            onDelete={toggleActive}
            onResetPassword={resetPassword}
          />
        )}
      </Stack>

      <CreateUserModal open={createOpen} onOpenChange={setCreateOpen} />
      <EditUserModal user={editing} onClose={() => setEditing(null)} />
    </PageShell>
  );
}
